import { Badge, Image, Text } from '@mantine/core';
import { useMantineTheme } from '@mantine/styles';
import positiveImg from '@/assets/images/feedback/positive.png';
import negativeImg from '@/assets/images/feedback/negative.png';
import { useTranslation } from 'react-i18next';

export interface CoreValueBadgeProps {
    /**
     * 핵심가치 이름(레이블)
     */
    label: string;
    /**
     * 배지 색상
     * @default 'blue'
     */
    colorName?: string;
    /**
     * 배지 크기
     */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /**
     * 선택되었는지 여부 - true일 경우 테두리 표시
     * @default false
     */
    isSelected?: boolean;
    /**
     * 비활성화 여부 - true일 경우 투명도 적용
     * @default false
     */
    disabled?: boolean;
    /**
     * 클릭 가능 여부 - true일 경우 cursor: pointer 적용
     * @default false
     */
    clickable?: boolean;
    /**
     * 아이콘 표시 여부
     * @default false
     */
    withIcon?: boolean;
}

/**
 * 워크스페이스 핵심가치 뱃지 컴포넌트
 */
export const CoreValueBadge = ({
    label,
    colorName = 'blue',
    size = 'md',
    isSelected = false,
    disabled = false,
    clickable = false,
    withIcon = false,
}: CoreValueBadgeProps) => {
    const { t } = useTranslation('feedback');
    const theme = useMantineTheme();
    const isNegative = colorName === 'red';

    return (
        <Badge
            size={size}
            variant="filled"
            style={{
                backgroundColor: theme.colors[colorName][0],
                color: isSelected
                    ? theme.colors[colorName][4]
                    : theme.colors[colorName][6],
                border: isSelected
                    ? `1px solid ${theme.colors[colorName][6]}`
                    : 'none',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled
                    ? 'not-allowed'
                    : clickable
                      ? 'pointer'
                      : 'default',
            }}
            styles={{
                root: {
                    padding: '4px 12px',
                    height: 'auto',
                    lineHeight: 'normal',
                },
                section: {
                    display: 'flex',
                    alignItems: 'center',
                },
            }}
            leftSection={
                withIcon && (
                    <Image
                        src={isNegative ? negativeImg : positiveImg}
                        width={13}
                        height={13}
                        alt={
                            isNegative
                                ? t('coreValue.negative')
                                : t('coreValue.positive')
                        }
                    />
                )
            }
        >
            <Text size="xs" fw={600}>
                {label}
            </Text>
        </Badge>
    );
};
